import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

/**
 * A content surface.
 *
 * No ng-primitives equivalent — a card is a box, with no behaviour to
 * abstract.
 *
 * The title renders as plain text rather than an <h*>, deliberately: a
 * card does not know how deep in the page it sits, and guessing a
 * heading level is worse for screen reader navigation than leaving the
 * outline to the page. Callers that want it in the outline can project
 * their own heading instead of passing `header`.
 */
@Component({
  selector: 'hh-card',
  standalone: true,
  template: `
    <div [class]="classes()">
      <div class="hh-card__body">
        @if (header()) {
          <div class="hh-card__title">{{ header() }}</div>
        }
        <ng-content />
      </div>
    </div>
  `,
  styleUrl: './hh-card.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HhCardComponent {
  readonly header = input('');

  /**
   * 'light' (default) and 'dark' are the app's own two sanctioned
   * surfaces — cream/phone and navy/big-screen. 'pop' adds a thick ink
   * outline and a bigger hard shadow for game-board-tile moments
   * (prompt cards, score tiles).
   */
  readonly variant = input<'light' | 'dark' | 'pop'>('light');

  protected readonly classes = computed(() => `hh-card hh-card--${this.variant()}`);
}
